import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  Slider,
  Button,
} from '@mui/material';
import {
  Close,
  PlayArrow,
  Pause,
  ContentCut,
  SkipPrevious,
  SkipNext,
} from '@mui/icons-material';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { revokeBlobUrl } from '../Utils/blobUrlUtils';
import { fetchFile } from '@ffmpeg/util';

interface VideoTrimmerProps {
  file: File;
  onTrimmed: (trimmedFile: File, originalSize: number, trimmedSize: number) => void;
  onCancel: () => void;
  maxDuration?: number;
  defaultSegmentDuration?: number;
  enableAutoCompression?: boolean;
}

interface TrimSegment {
  start: number;
  end: number;
  duration: number;
}

const VideoTrimmer: React.FC<VideoTrimmerProps> = ({
  file,
  onTrimmed,
  onCancel,
  maxDuration = 30,
  defaultSegmentDuration = 30,
  enableAutoCompression = true,
}) => {
  const [ffmpeg, setFfmpeg] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [trimStatus, setTrimStatus] = useState('');
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoInfo, setVideoInfo] = useState<{
    duration: number;
    size: number;
    resolution: string;
    width: number;
    height: number;
  } | null>(null);
  const [error, setError] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [trimSegment, setTrimSegment] = useState<TrimSegment>({
    start: 0,
    end: defaultSegmentDuration,
    duration: defaultSegmentDuration,
  });
  const [timelineFrames, setTimelineFrames] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState<'start' | 'end' | 'segment' | null>(null);
  const [trimmedFile, setTrimmedFile] = useState<File | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [compressionStatus, setCompressionStatus] = useState('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Initialize FFmpeg
  useEffect(() => {
    const initFFmpeg = async () => {
      try {
        setTrimStatus('🔄 Loading FFmpeg...');
        const ffmpegInstance = new FFmpeg();
        
        ffmpegInstance.on('log', ({ message }) => {
          console.log('FFmpeg Log:', message);
        });

        try {
          await ffmpegInstance.load();
          setFfmpeg(ffmpegInstance);
          setTrimStatus('✅ FFmpeg loaded successfully');
        } catch (defaultError) {
          await ffmpegInstance.load({
            coreURL: 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/ffmpeg-core.js',
            wasmURL: 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/ffmpeg-core.wasm',
          });
          setFfmpeg(ffmpegInstance);
          setTrimStatus('✅ FFmpeg loaded successfully');
        }
      } catch (err) {
        setError(`❌ Failed to load FFmpeg: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };

    initFFmpeg();
  }, []);

  // Load video and generate timeline frames
  useEffect(() => {
    const loadVideo = async () => {
      if (!file) return;

      const url = URL.createObjectURL(file);
      setVideoUrl(url);

      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        const duration = video.duration;
        const size = file.size;
        const resolution = `${video.videoWidth}x${video.videoHeight}`;

        setVideoInfo({
          duration,
          size,
          resolution,
          width: video.videoWidth,
          height: video.videoHeight,
        });

        // Adjust trim segment to fit video duration
        const adjustedEnd = Math.min(defaultSegmentDuration, duration);
        setTrimSegment({
          start: 0,
          end: adjustedEnd,
          duration: adjustedEnd,
        });

        // Generate timeline frames
        generateTimelineFrames(video, duration);
      };

      video.src = url;
    };

    loadVideo();
  }, [file, defaultSegmentDuration]);

  useEffect(() => {
    return () => {
      revokeBlobUrl(videoUrl);
    };
  }, [videoUrl]);

  // Generate timeline preview frames
  const generateTimelineFrames = async (video: HTMLVideoElement, duration: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const frameCount = 20; // Number of frames to show in timeline
    const frames: string[] = [];

    for (let i = 0; i < frameCount; i++) {
      const time = (duration / frameCount) * i;
      video.currentTime = time;
      
      await new Promise<void>((resolve) => {
        video.onseeked = () => {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const frameDataUrl = canvas.toDataURL('image/jpeg', 0.3);
          frames.push(frameDataUrl);
          resolve();
        };
      });
    }

    setTimelineFrames(frames);
  };

  // Video playback controls
  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const seekTo = (time: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const skipToStart = () => seekTo(trimSegment.start);
  const skipToEnd = () => seekTo(trimSegment.end);

  // Timeline drag handlers
  const handleTimelineMouseDown = (e: React.MouseEvent, type: 'start' | 'end' | 'segment') => {
    setIsDragging(true);
    setDragType(type);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !timelineRef.current || !videoInfo) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const time = percentage * videoInfo.duration;

    if (dragType === 'start') {
      const newStart = Math.min(time, trimSegment.end - 1);
      setTrimSegment(prev => ({
        ...prev,
        start: newStart,
        duration: prev.end - newStart,
      }));
    } else if (dragType === 'end') {
      const newEnd = Math.max(time, trimSegment.start + 1);
      setTrimSegment(prev => ({
        ...prev,
        end: newEnd,
        duration: newEnd - prev.start,
      }));
    } else if (dragType === 'segment') {
      const segmentWidth = trimSegment.duration / videoInfo.duration;
      const newStart = Math.max(0, Math.min(videoInfo.duration - trimSegment.duration, time - (segmentWidth * videoInfo.duration) / 2));
      const newEnd = newStart + trimSegment.duration;
      setTrimSegment({
        start: newStart,
        end: newEnd,
        duration: trimSegment.duration,
      });
    }
  }, [isDragging, dragType, trimSegment, videoInfo]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragType(null);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  // Update current time during playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, []);

  // Trim video using FFmpeg
  const trimVideo = async () => {
    if (!ffmpeg || !file || !videoInfo) return;

    setIsLoading(true);
    setProgress(0);
    setError('');
    setTrimStatus('Starting video trim...');

    try {
      // Write input file
      setTrimStatus('Loading video file...');
      await ffmpeg.writeFile('input.mp4', await fetchFile(file));

      // Set up progress monitoring
      ffmpeg.on('progress', ({ progress }: { progress: number }) => {
        setProgress(progress * 100);
        setTrimStatus(`Trimming... ${Math.round(progress * 100)}%`);
      });

      // Calculate trim parameters
      const startTime = trimSegment.start;
      const duration = trimSegment.duration;

      // Run trim command
      setTrimStatus('Trimming video...');
      await ffmpeg.exec([
        '-i', 'input.mp4',
        '-ss', startTime.toString(),
        '-t', duration.toString(),
        '-c', 'copy', // Fast copy without re-encoding
        '-avoid_negative_ts', 'make_zero',
        'output.mp4'
      ]);

      // Read output file
      setTrimStatus('Finalizing...');
      const data = await ffmpeg.readFile('output.mp4');
      const trimmedBlob = new Blob([data], { type: 'video/mp4' });
      const trimmedFile = new File([trimmedBlob], file.name.replace(/\.[^/.]+$/, '_trimmed.mp4'), {
        type: 'video/mp4',
      });

      // Calculate size reduction
      const originalSize = file.size;
      const trimmedSize = trimmedFile.size;
      const reduction = ((originalSize - trimmedSize) / originalSize * 100).toFixed(1);

      setTrimStatus(`Trim complete! Size reduced by ${reduction}%`);
      setProgress(100);

      // Store trimmed file for potential compression
      setTrimmedFile(trimmedFile);

      // Auto-start compression if enabled
      if (enableAutoCompression) {
        setTimeout(() => {
          compressTrimmedVideo(trimmedFile, originalSize);
        }, 1000); // Small delay to show trim completion
      } else {
        // Call callback with trimmed file if no auto-compression
        onTrimmed(trimmedFile, originalSize, trimmedSize);
      }

    } catch (err) {
      setError(`Trim failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Trim error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const compressTrimmedVideo = async (trimmedFile: File, originalSize: number) => {
    if (!ffmpeg || !trimmedFile) return;

    setIsCompressing(true);
    setCompressionProgress(0);
    setCompressionStatus('Starting compression...');

    try {
      // Write trimmed file
      setCompressionStatus('Loading trimmed video...');
      await ffmpeg.writeFile('trimmed_input.mp4', await fetchFile(trimmedFile));

      // Set up progress monitoring
      ffmpeg.on('progress', ({ progress }: { progress: number }) => {
        setCompressionProgress(progress * 100);
        setCompressionStatus(`Compressing... ${Math.round(progress * 100)}%`);
      });

      // Run compression command with orientation preservation
      setCompressionStatus('Compressing video...');
      
      // Detect video orientation and set appropriate scaling
      const isPortrait = videoInfo && videoInfo.height > videoInfo.width;
      let scaleFilter: string;
      
      if (isPortrait) {
        // For portrait videos, maintain portrait orientation
        // Scale to fit within 720x1280 (portrait) or maintain aspect ratio
        scaleFilter = 'scale=720:1280:force_original_aspect_ratio=decrease,pad=720:1280:(ow-iw)/2:(oh-ih)/2';
      } else {
        // For landscape videos, maintain landscape orientation
        scaleFilter = 'scale=1280:720:force_original_aspect_ratio=decrease,pad=1280:720:(ow-iw)/2:(oh-ih)/2';
      }
      
      console.log('Video orientation:', isPortrait ? 'Portrait' : 'Landscape');
      console.log('Scale filter:', scaleFilter);
      
      await ffmpeg.exec([
        '-i', 'trimmed_input.mp4',
        '-c:v', 'libx264',
        '-preset', 'ultrafast',
        '-crf', '25',
        '-b:v', '800k',
        '-maxrate', '800k',
        '-bufsize', '800k',
        '-vf', scaleFilter,
        '-c:a', 'aac',
        '-b:a', '128k',
        '-ar', '44100',
        '-movflags', '+faststart',
        '-profile:v', 'baseline',
        '-level', '3.1',
        '-x264opts', 'ref=1:bframes=0',
        '-threads', '0',
        'compressed_output.mp4'
      ]);

      // Read compressed file
      setCompressionStatus('Finalizing compression...');
      const data = await ffmpeg.readFile('compressed_output.mp4');
      const compressedBlob = new Blob([data], { type: 'video/mp4' });
      const compressedFile = new File([compressedBlob], trimmedFile.name.replace(/\.[^/.]+$/, '_compressed.mp4'), {
        type: 'video/mp4',
      });

      // Calculate final size reduction
      const compressedSize = compressedFile.size;
      const totalReduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
      const compressionReduction = ((trimmedFile.size - compressedSize) / trimmedFile.size * 100).toFixed(1);

      setCompressionStatus(`Compression complete! Total reduction: ${totalReduction}%`);
      setCompressionProgress(100);

      // Call callback with final compressed file
      onTrimmed(compressedFile, originalSize, compressedSize);

    } catch (err) {
      setError(`Compression failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Compression error:', err);
      // Fallback to trimmed file if compression fails
      onTrimmed(trimmedFile, originalSize, trimmedFile.size);
    } finally {
      setIsCompressing(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!videoInfo) {
    return (
      <Card sx={{ maxWidth: 800, width: '100%', mx: 'auto' }}>
        <CardContent>
          <Typography>Loading video...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 800, width: '100%', mx: 'auto' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Video Trimmer
          </Typography>
          <IconButton onClick={onCancel} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Video Info */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Video Info
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label={`Duration: ${formatTime(videoInfo.duration)}`} size="small" />
            <Chip label={`Size: ${formatFileSize(videoInfo.size)}`} size="small" />
            <Chip label={`Resolution: ${videoInfo.resolution}`} size="small" />
            <Chip label={`Segment: ${formatTime(trimSegment.duration)}`} size="small" color="primary" />
          </Box>
        </Box>

        {/* Video Player */}
        <Box sx={{ mb: 3 }}>
          <video
            ref={videoRef}
            src={videoUrl}
            style={{ width: '100%', maxHeight: 300, borderRadius: 4 }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
          
          {/* Video Controls */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'center' }}>
            <IconButton onClick={togglePlay} size="small">
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <IconButton onClick={skipToStart} size="small">
              <SkipPrevious />
            </IconButton>
            <IconButton onClick={skipToEnd} size="small">
              <SkipNext />
            </IconButton>
          </Box>
        </Box>

        {/* Timeline */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Timeline - Drag handles to select segment
          </Typography>
          
          <Box
            ref={timelineRef}
            sx={{
              position: 'relative',
              height: 80,
              bgcolor: 'grey.100',
              borderRadius: 1,
              overflow: 'hidden',
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
            onMouseDown={(e) => handleTimelineMouseDown(e, 'segment')}
          >
            {/* Timeline Frames */}
            <Box sx={{ display: 'flex', height: '100%' }}>
              {timelineFrames.map((frame, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: 1,
                    backgroundImage: `url(${frame})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRight: '1px solid rgba(255,255,255,0.3)',
                  }}
                />
              ))}
            </Box>

            {/* Current Time Indicator */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${(currentTime / videoInfo.duration) * 100}%`,
                width: 2,
                bgcolor: 'red',
                zIndex: 3,
              }}
            />

            {/* Trim Segment */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${(trimSegment.start / videoInfo.duration) * 100}%`,
                width: `${(trimSegment.duration / videoInfo.duration) * 100}%`,
                bgcolor: 'rgba(25, 118, 210, 0.3)',
                border: '2px solid #1976d2',
                zIndex: 2,
              }}
            />

            {/* Start Handle */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${(trimSegment.start / videoInfo.duration) * 100}%`,
                width: 8,
                bgcolor: '#1976d2',
                cursor: 'ew-resize',
                zIndex: 4,
                '&:hover': { bgcolor: '#1565c0' },
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleTimelineMouseDown(e, 'start');
              }}
            />

            {/* End Handle */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${(trimSegment.end / videoInfo.duration) * 100}%`,
                width: 8,
                bgcolor: '#1976d2',
                cursor: 'ew-resize',
                zIndex: 4,
                '&:hover': { bgcolor: '#1565c0' },
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleTimelineMouseDown(e, 'end');
              }}
            />
          </Box>

          {/* Time Labels */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              {formatTime(trimSegment.start)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatTime(trimSegment.end)}
            </Typography>
          </Box>
        </Box>

        {/* Duration Slider */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Segment Duration: {formatTime(trimSegment.duration)}
          </Typography>
          <Slider
            value={trimSegment.duration}
            onChange={(_, value) => {
              const newDuration = value as number;
              const newEnd = Math.min(trimSegment.start + newDuration, videoInfo.duration);
              setTrimSegment({
                start: trimSegment.start,
                end: newEnd,
                duration: newDuration,
              });
            }}
            min={1}
            max={Math.min(maxDuration, videoInfo.duration)}
            step={1}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => formatTime(value)}
          />
        </Box>

        {/* Progress */}
        {isLoading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              {trimStatus}
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
              {Math.round(progress)}% Complete
            </Typography>
          </Box>
        )}

        {/* Compression Progress */}
        {isCompressing && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              {compressionStatus}
            </Typography>
            <LinearProgress variant="determinate" value={compressionProgress} color="secondary" />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
              {Math.round(compressionProgress)}% Complete
            </Typography>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<ContentCut />}
            onClick={trimVideo}
            disabled={isLoading || isCompressing || !ffmpeg || !!error}
            sx={{ minWidth: 120 }}
          >
            {isLoading ? 'Trimming...' : isCompressing ? 'Compressing...' : 'Trim & Compress'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isLoading || isCompressing}
          >
            Cancel
          </Button>
        </Box>

        {/* Hidden canvas for frame generation */}
        <canvas
          ref={canvasRef}
          width={160}
          height={90}
          style={{ display: 'none' }}
        />
      </CardContent>
    </Card>
  );
};

export default VideoTrimmer;
